import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./button";

export type ToastVariant = "default" | "destructive" | "success";

export interface Toast {
  id: string;
  message: string;
  variant?: ToastVariant;
  duration?: number;
}

interface ToastContextType {
  showToast: (
    message: string,
    variant?: ToastVariant,
    duration?: number,
  ) => void;
  toasts: Toast[];
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, variant: ToastVariant = "default", duration = 5000) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      const newToast: Toast = { id, message, variant, duration };

      setToasts((prev) => {
        const updated = [...prev, newToast];
        // Limit to 2 toasts - remove oldest if we exceed the limit
        return updated.slice(-2);
      });

      // Auto-dismiss after duration
      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }
    },
    [removeToast],
  );

  return (
    <ToastContext.Provider value={{ showToast, toasts, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  removeToast: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  removeToast,
}) => {
  if (toasts.length === 0) return null;

  return createPortal(
    <div className="fixed bottom-0 right-0 z-[2147483647] flex flex-col gap-2 p-4 max-w-md w-full pointer-events-none">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
      ))}
    </div>,
    document.body,
  );
};

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation on mount
    setIsVisible(true);
  }, []);

  const handleRemove = () => {
    setIsVisible(false);
    // Wait for animation to complete before removing
    setTimeout(() => {
      onRemove(toast.id);
    }, 200);
  };

  const variantStyles = {
    default: "bg-card border-border text-card-foreground",
    destructive:
      "bg-destructive border-destructive text-destructive-foreground",
    success: "bg-bauhaus-sage border-bauhaus-sage text-bauhaus-cream",
  };

  return (
    <div
      className={cn(
        "pointer-events-auto rounded-lg border shadow-lg p-4 flex items-start gap-3 transition-all duration-200",
        variantStyles[toast.variant || "default"],
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2",
      )}
    >
      <div className="flex-1 text-sm">{toast.message}</div>
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          "h-6 w-6 p-0 hover:bg-foreground/10",
          toast.variant === "destructive" || toast.variant === "success"
            ? "text-bauhaus-cream"
            : "text-muted-foreground",
        )}
        onClick={handleRemove}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};
